import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuthStore } from '@/stores/useAuthStore';
import { canAccess, DEFINE_PASSWORD_PATH, getHomePath, isCompanyScoped } from '@/security/navigation';
import { useAccessRoles } from '@/security/useAccessRoles';
import { useCompanyStore } from '@/stores/useCompanyStore';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import { Button } from '@/components/ui/button';

/**
 * Redirige vers /login sans session, et vers la définition du mot de passe local tant qu'il n'est pas défini
 * (wiki « Définition du password local » : utilisateur connecté via SSO).
 * L'utilisateur (nom, rôle, passwordDefined) est chargé par GET /users/me : « Chargement… » en attendant,
 * et proposition de réessayer si l'API est injoignable.
 */
export function RequireAuth() {
  const session = useAuthStore((state) => state.session);
  const user = useAuthStore((state) => state.user);
  const loadError = useAuthStore((state) => state.loadError);
  const loadCurrentUser = useAuthStore((state) => state.loadCurrentUser);
  const location = useLocation();

  useEffect(() => {
    if (session && !user && !loadError) loadCurrentUser();
  }, [session, user, loadError, loadCurrentUser]);

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        {loadError ? (
          <>
            <p role="alert">Impossible de joindre le serveur.</p>
            <Button variant="outline" onClick={() => loadCurrentUser()}>
              Réessayer
            </Button>
          </>
        ) : (
          <p role="status">Chargement…</p>
        )}
      </div>
    );
  }

  // Process front seulement : l'API ne bloque rien sans mot de passe local
  const onDefinePage = location.pathname === DEFINE_PASSWORD_PATH;
  if (!user.passwordDefined && !onDefinePage) {
    return <Navigate to={DEFINE_PASSWORD_PATH} replace />;
  }
  if (user.passwordDefined && onDefinePage) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

/**
 * Affiche la page uniquement si un des rôles de l'utilisateur y donne accès.
 * Page d'un rôle dans la société (Équipes, Employés : Dirigeant) : décidé une fois les sociétés chargées ; sans ce rôle
 * dans la société sélectionnée (ex. après en avoir changé), retour à la page d'accueil plutôt qu'un refus.
 */
export function RequireAccess() {
  const roles = useAccessRoles();
  const companiesLoaded = useCompanyStore((state) => state.loaded);
  const { pathname } = useLocation();

  if (canAccess(pathname, roles)) return <Outlet />;
  if (!isCompanyScoped(pathname)) return <ForbiddenPage />;
  if (!companiesLoaded) return <p className="text-sm text-slate-500">Chargement…</p>;

  const home = getHomePath(roles);
  return home ? <Navigate to={home} replace /> : <ForbiddenPage />;
}

/**
 * "/" : redirige vers la page d'accueil du rôle, une fois les sociétés chargées (la Feuille d'heure dépend du rôle dans
 * la société sélectionnée)
 */
export function HomeRedirect() {
  const roles = useAccessRoles();
  const companiesLoaded = useCompanyStore((state) => state.loaded);

  if (!companiesLoaded) return <p className="text-sm text-slate-500">Chargement…</p>;

  const home = getHomePath(roles);

  return home ? <Navigate to={home} replace /> : <ForbiddenPage />;
}
